import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ErrorComponent from "../+global/ErrorComponent";
import NewColony from "../+global/modals/NewColony";
import Get from "../../helpers/ajax/get";
import Put from "../../helpers/ajax/put";
import { GiFamilyHouse } from "react-icons/gi";

// Format a "MM-DD" deadline as e.g. "November 15"
const formatDeadline = (deadline) => {
    const [month, day] = (deadline || "").split("-").map(Number);
    if (!month || !day) return "";
    return new Date(2000, month - 1, day).toLocaleDateString("en-US", { month: "long", day: "numeric" });
};

const Home = () => {
    document.title = "Home | Dependable Admin";

    const [hasError, setHasError] = useState(null);
    const [colonies, setColonies] = useState([]);
    const [addColonyOpen, setAddColonyOpen] = useState(false);
    // Global settings (apply to every colony at once)
    const [deadline, setDeadline] = useState("");
    // Last saved value — the Save button only shows when `deadline` differs
    const [savedDeadline, setSavedDeadline] = useState("");
    const [savingSettings, setSavingSettings] = useState(false);
    const [settingsMessage, setSettingsMessage] = useState(null);
    const navigate = useNavigate();

    const getColonies = async () => {
        try {
            var colonies = await Get("/api/colonies");
            setColonies(colonies.data);
        } catch (error) {
            setHasError(error.error || "an error occurred");
        }
    };

    const getSettings = async () => {
        try {
            const settings = await Get("/api/settings");
            // The deadline is stored as "MM-DD"; show it with the current year
            const loaded = `${new Date().getFullYear()}-${settings.winterizeDeadline}`;
            setDeadline(loaded);
            setSavedDeadline(loaded);
        } catch (error) {
            setSettingsMessage({ type: "error", text: error.message || "an error occurred" });
        }
    };

    const saveSettings = async () => {
        if (!deadline) return;
        setSavingSettings(true);
        setSettingsMessage(null);
        try {
            const settings = await Put("/api/settings", { winterizeDeadline: deadline.slice(5) });
            const saved = `${new Date().getFullYear()}-${settings.winterizeDeadline}`;
            setDeadline(saved);
            setSavedDeadline(saved);
            setSettingsMessage({ type: "success", text: "Saved" });
        } catch (error) {
            setSettingsMessage({ type: "error", text: error.message || "an error occurred" });
        } finally {
            setSavingSettings(false);
        }
    };

    useEffect(() => {
        getColonies();
        getSettings();
    }, []);

    if (!!hasError) return <ErrorComponent error={hasError} />;

    return (
        <div className="max-w-6xl mx-auto text-left">
            <div className="rounded-lg shadow-md border p-5 mb-8">
                <div className="grid grid-cols-12 md:gap-8 items-center">
                    <div className="md:col-span-4 col-span-12 self-start">
                        <div className="font-semibold">Winterization deadline</div>
                        <p className="mt-2 text-sm">
                            Requests are only accepted on or before this date. The month and day apply to every colony and repeat each year
                            {deadline ? ` (currently ${formatDeadline(deadline.slice(5))})` : ""}.
                        </p>
                    </div>
                    <div className="md:col-span-5 col-span-12">
                        <input
                            type="date"
                            value={deadline}
                            onChange={(e) => { setDeadline(e.target.value); setSettingsMessage(null); }}
                            className="focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold"
                        />
                    </div>
                    <div className="md:col-span-3 col-span-12">
                        {!!deadline && deadline !== savedDeadline && (
                            <button
                                disabled={savingSettings}
                                onClick={saveSettings}
                                className="disabled:opacity-50 bg-dependable text-white py-3 px-5 rounded-md"
                            >
                                {savingSettings ? "Saving..." : "Save"}
                            </button>
                        )}
                        {!!settingsMessage && (
                            <p className={`mt-2 text-sm ${settingsMessage.type === "error" ? "text-red-700" : "text-dependable-green"}`}>
                                {settingsMessage.text}
                            </p>
                        )}
                    </div>
                </div>
            </div>
            <div className="py-5">
                <h2 className="text-4xl font-bold inline-block">Colonies</h2>
                <button className="float-right bg-dependable text-white py-3 px-5 rounded-md " onClick={() => setAddColonyOpen(true)}>
                    Add colony
                </button>
                <Link to="/signupdata" className="mr-4 float-right border border-depenable text-dependable py-3 px-5 rounded-md">
                    See signup data
                </Link>
            </div>
            <div className="grid grid-cols-4 py-5 gap-8">
                {colonies.map((colony) => {
                    return (
                        <Link to={`/colony/${colony.colony_id}`} key={colony.colony_id} className="rounded-lg shadow-md hover:shadow-xl duration-150 border p-5 text-center col-span-2 md:col-span-1">
                            <GiFamilyHouse className="m-auto text-7xl text-[#14518e]" />
                            <p className="m-auto text-xl mt-3">{colony.name}</p>
                        </Link>
                    );
                })}
            </div>
            {addColonyOpen && (
                <NewColony
                    close={() => setAddColonyOpen(false)}
                    successCallback={(colony_id) => {
                        navigate(`/colony/${colony_id}`);
                    }}
                />
            )}
        </div>
    );
};

export default Home;
